import * as React from 'react';
import { Button, Input, Label, Modal, Switch, TextArea, TextField } from '@heroui/react';
import { MultipleChoiceControl, NumberControl, SingleChoiceControl, TextControl } from './control';
import { numberToLetterId } from './utils';
import type {
  MultipleChoiceQuestion,
  NumberQuestion,
  Question,
  QuestionnaireType,
  QuestionType,
  SingleChoiceQuestion,
  TextQuestion,
} from './types';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

const TYPE_LABELS: Record<QuestionType, string> = {
  single_choice: '单选题',
  multiple_choice: '多选题',
  text: '文本题',
  number: '数字题',
  rating: '评分题',
  true_false: '判断题',
  sorting: '排序题',
  matrix: '矩阵题',
  date: '日期题',
  file_upload: '文件上传题',
  poll: '投票题',
};

const ALL_TYPES: { label: string; value: QuestionType }[] = Object.entries(TYPE_LABELS).map(([value, label]) => ({
  value: value as QuestionType,
  label,
}));

function createQuestionByType(type: QuestionType, existing: Question): Partial<Question> {
  const base = {
    id: existing.id,
    title: existing.title,
    description: existing.description,
    required: existing.required,
  };

  switch (type) {
    case 'single_choice':
      return {
        ...base,
        type: 'single_choice',
        options:
          (existing as SingleChoiceQuestion).options?.length >= 2
            ? (existing as SingleChoiceQuestion).options
            : [
                { id: numberToLetterId(1), label: '', value: '' },
                { id: numberToLetterId(2), label: '', value: '' },
              ],
        minSelections: undefined,
        maxSelections: undefined,
        correctAnswers: undefined,
        correctAnswer: (existing as SingleChoiceQuestion).correctAnswer,
      } as Partial<SingleChoiceQuestion>;
    case 'multiple_choice':
      return {
        ...base,
        type: 'multiple_choice',
        options:
          (existing as MultipleChoiceQuestion).options?.length >= 2
            ? (existing as MultipleChoiceQuestion).options
            : [
                { id: numberToLetterId(1), label: '', value: '' },
                { id: numberToLetterId(2), label: '', value: '' },
              ],
        minSelections: undefined,
        maxSelections: undefined,
        correctAnswer: undefined,
        correctAnswers: (existing as MultipleChoiceQuestion).correctAnswers || [],
      } as Partial<MultipleChoiceQuestion>;
    case 'text':
      return {
        ...base,
        type: 'text',
        placeholder: (existing as TextQuestion).placeholder,
        minLength: (existing as TextQuestion).minLength,
        maxLength: (existing as TextQuestion).maxLength,
      } as Partial<TextQuestion>;
    case 'number':
      return {
        ...base,
        type: 'number',
        min: (existing as NumberQuestion).min,
        max: (existing as NumberQuestion).max,
        step: (existing as NumberQuestion).step,
      } as Partial<NumberQuestion>;
    default:
      return { ...base, type } as Partial<Question>;
  }
}

export interface QuestionEditorProps {
  question: Question;
  questionIndex?: number;
  questionnaireType?: QuestionnaireType;
  disabled?: boolean;
  onUpdate: (updates: Partial<Question>) => void;
  onRemove: () => void;
}

export function QuestionEditor({
  question,
  questionIndex,
  questionnaireType = 'survey',
  disabled,
  onUpdate,
  onRemove,
}: QuestionEditorProps) {
  const [typeOpen, setTypeOpen] = React.useState(false);
  const titleLabel = questionIndex ? `第${questionIndex}题` : '题目';

  const showCorrectAnswerResolved = questionnaireType === 'quiz' || questionnaireType === 'exam';
  const showScore = questionnaireType === 'quiz' || questionnaireType === 'exam';
  const showExplanation = questionnaireType === 'quiz' || questionnaireType === 'exam';

  const handleTypePick = (newType: QuestionType) => {
    if (newType === question.type) {
      setTypeOpen(false);
      return;
    }
    onUpdate(createQuestionByType(newType, question) as Partial<Question>);
    setTypeOpen(false);
  };

  let control: React.ReactNode = null;
  if (question.type === 'single_choice') {
    control = (
      <SingleChoiceControl
        question={question as SingleChoiceQuestion}
        disabled={disabled}
        showCorrectAnswer={showCorrectAnswerResolved}
        showScore={showScore}
        questionnaireType={questionnaireType}
        onUpdate={onUpdate}
      />
    );
  } else if (question.type === 'multiple_choice') {
    control = (
      <MultipleChoiceControl
        question={question as MultipleChoiceQuestion}
        disabled={disabled}
        showCorrectAnswer={showCorrectAnswerResolved}
        showScore={showScore}
        questionnaireType={questionnaireType}
        onUpdate={onUpdate}
      />
    );
  } else if (question.type === 'text') {
    control = <TextControl question={question as TextQuestion} disabled={disabled} onUpdate={onUpdate} />;
  } else if (question.type === 'number') {
    control = (
      <NumberControl
        question={question as NumberQuestion}
        disabled={disabled}
        showCorrectAnswer={showCorrectAnswerResolved}
        showScore={showScore}
        onUpdate={onUpdate}
      />
    );
  } else {
    control = (
      <p className="text-sm text-muted-foreground">
        该题型暂未提供可视化编辑，请使用单选、多选、文本或数字题；或直接通过 JSON 维护 schema。
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h5 className="font-medium text-foreground">{titleLabel}</h5>
        <Button type="button" variant="ghost" size="sm" onPress={onRemove} isDisabled={disabled}>
          删除
        </Button>
      </div>

      <div className="space-y-2">
        <div className="text-sm font-medium text-foreground">选择题型</div>
        <Button type="button" variant="secondary" size="sm" onPress={() => setTypeOpen(true)} isDisabled={disabled}>
          {TYPE_LABELS[question.type] || '未知类型'}
        </Button>
      </div>

      <TextField className="w-full" isDisabled={disabled} isRequired>
        <Label htmlFor={`question-title-${question.id}`}>
          题干<span className="text-danger ml-1">*</span>
        </Label>
        <Input
          id={`question-title-${question.id}`}
          value={question.title}
          onChange={(e) => onUpdate({ title: e.target.value })}
          placeholder="请输入题干"
        />
      </TextField>

      <TextField className="w-full" isDisabled={disabled}>
        <Label htmlFor={`question-description-${question.id}`}>题目描述</Label>
        <TextArea
          id={`question-description-${question.id}`}
          value={question.description ?? ''}
          onChange={(e) => onUpdate({ description: e.target.value })}
          placeholder="输入题目的说明（可选）"
          rows={2}
        />
      </TextField>

      <div className="flex items-center gap-3">
        <Switch isSelected={question.required} onChange={(v) => onUpdate({ required: v })} isDisabled={disabled}>
          <Switch.Content>
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
          </Switch.Content>
        </Switch>
        <span className="text-sm text-foreground">必填</span>
      </div>

      {control}

      {showExplanation ? (
        <TextField className="w-full" isDisabled={disabled}>
          <Label htmlFor={`question-explanation-${question.id}`}>答案解析</Label>
          <TextArea
            id={`question-explanation-${question.id}`}
            value={question.explanation ?? ''}
            onChange={(e) => onUpdate({ explanation: e.target.value })}
            placeholder="填写答案解析，仅在测验/考试中使用"
            rows={3}
          />
        </TextField>
      ) : null}

      <Modal isOpen={typeOpen} onOpenChange={setTypeOpen}>
        <RacDialogTriggerAnchor />
        <Modal.Backdrop>
          <Modal.Container placement="center" size="md">
            <Modal.Dialog className="max-w-md">
              <Modal.Header>
                <Modal.Heading>选择题型</Modal.Heading>
              </Modal.Header>
              <Modal.Body>
                <div className="grid grid-cols-2 gap-2">
                  {ALL_TYPES.map((t) => (
                    <Button
                      key={t.value}
                      type="button"
                      variant="secondary"
                      size="sm"
                      onPress={() => handleTypePick(t.value)}
                      isDisabled={disabled}
                    >
                      {t.label}
                    </Button>
                  ))}
                </div>
              </Modal.Body>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      </Modal>
    </div>
  );
}
